import React, { useState } from 'react'
import moment from 'moment'
import { Badge } from 'components/ds/Badge'
import Button, { IconButton } from 'components/ds/Button'
import Icon from 'components/ds/Icon'
import Skeleton from 'components/ds/Skeleton'
import EmptyState from 'components/ds/EmptyState'
import { Icon as AssetIcon } from 'styles'
import { NOT_REPORTED } from './fmcsaViewModel'
import styles from './FmcsaDetails.module.scss'

const cx = (...p) => p.filter(Boolean).join(' ')

export const DrawerHeading = ({ title, subtitle, onBack }) => (
    <span className={styles.header}>
        {onBack && (
            <IconButton label="Back to FMCSA Details" onClick={onBack}>
                <Icon name="arrow-left" />
            </IconButton>
        )}
        <span className={styles.headerText}>
            <span className={styles.headerTitle}>{title}</span>
            {subtitle && <span className={styles.headerSubtitle}>{subtitle}</span>}
        </span>
    </span>
)

export const Card = ({ title, className, children }) => (
    <section className={cx(styles.card, className)}>
        {title && <h3 className={styles.cardTitle}>{title}</h3>}
        <div className={styles.cardBody}>{children}</div>
    </section>
)

export const Field = ({ label, value, alert }) => (
    <div className={styles.field}>
        <span className={styles.fieldLabel}>{label}</span>
        <span className={cx(styles.fieldValue, value === NOT_REPORTED && styles.muted, alert && styles.alert)}>
            {value}
        </span>
    </div>
)

export const CreditCost = ({ cost }) => cost == null ? null : (
    <span className={styles.cost}>
        {cost}
        <AssetIcon folder="icons" fileName="credits.svg" />
    </span>
)

const Tile = ({ label, value }) => (
    <div className={styles.tile}>
        <span className={styles.eyebrow}>{label}</span>
        {value === NOT_REPORTED
            ? <div className={styles.tileValueMuted}>{value}</div>
            : <div className={styles.tileValue}>{value}</div>}
    </div>
)

const Policy = ({ row }) => (
    <div className={styles.policy}>
        <div className={styles.policyHead}>
            <div>
                <span className={styles.policyName}>{row.name}</span>
                <span className={styles.policyFull}>{row.full}</span>
            </div>
            {row.badge && <Badge tone={row.badge.tone}>{row.badge.label}</Badge>}
        </div>
        <div className={styles.policyFigures}>
            <div className={styles.figure}>
                <span className={styles.fieldLabel}>Required</span>
                <span className={cx(styles.figureValue, row.required === NOT_REPORTED && styles.muted)}>{row.required}</span>
            </div>
            <div className={styles.figure}>
                <span className={styles.fieldLabel}>On file</span>
                <span className={cx(styles.figureValue, row.short && styles.alert)}>{row.onFile}</span>
            </div>
            {row.requiredAmount && (
                <div className={styles.figure}>
                    <span className={styles.fieldLabel}>Required amount</span>
                    <span className={styles.figureValue}>{row.requiredAmount}</span>
                </div>
            )}
        </div>
        {row.bar && (
            <>
                <div className={styles.gauge} role="img" aria-label={`${row.name}: ${row.onFile} on file, ${row.requiredAmount} required`}>
                    <div className={cx(styles.gaugeFill, row.short ? styles.declined : styles.approved)} style={{ width: `${row.bar.fill}%` }} />
                    <span className={styles.gaugeTick} style={{ left: `${row.bar.tick}%` }} />
                </div>
                <div className={styles.gaugeNotes}>
                    <span className={row.short ? styles.noteDeclined : styles.noteApproved}>{row.note}</span>
                    <span>Tick marks the required amount</span>
                </div>
            </>
        )}
    </div>
)

const ExtensiveFooter = ({ extensive, readOnly, onOpen }) => {
    const [showAll, setShowAll] = useState(false)
    const { searches, latest, requesting, loadingDetail, extensiveCost } = extensive
    const busy = requesting || loadingDetail

    const requestButton = (label, variant) => !readOnly && (
        <Button variant={variant} loading={requesting} disabled={busy}
                onClick={async () => onOpen(await extensive.requestNew())}>
            {requesting ? 'Requesting...' : label}
            {!requesting && <CreditCost cost={extensiveCost} />}
        </Button>
    )

    if (searches.length === 0) {
        return (
            <section className={cx(styles.card, styles.footer)} aria-label="Extensive FMCSA data">
                <div className={styles.footerRow}>
                    <div>
                        <span className={styles.footerTitle}>Extensive FMCSA data</span>
                        <span className={styles.verdictDetail}>MC numbers, BASICs scores, cargo and out-of-service history</span>
                    </div>
                    {requestButton('Request extensive FMCSA data', 'primary')}
                </div>
            </section>
        )
    }

    const rows = showAll ? searches : searches.slice(0, 3)
    return (
        <section className={cx(styles.card, styles.footer)} aria-label="Extensive FMCSA data">
            <div className={styles.footerRow}>
                <div>
                    <span className={styles.footerTitle}>Extensive FMCSA data</span>
                    <span className={styles.verdictDetail}>
                        {latest ? `Latest report ${moment(latest.createdAt).format('MMM D, YYYY')}` : 'No completed report yet'}
                    </span>
                </div>
                <div className={styles.footerActions}>
                    {requestButton('Request new', 'secondary')}
                    {latest && (
                        <Button disabled={busy} onClick={async () => onOpen(await extensive.open(latest))}>
                            View latest extensive report
                        </Button>
                    )}
                </div>
            </div>
            <div className={styles.history}>
                <span className={styles.eyebrow}>Request history</span>
                {rows.map(search => {
                    const done = extensive.isCompleted(search)
                    return (
                        <button key={search.uuid} type="button" className={styles.historyRow}
                                disabled={!done || busy}
                                onClick={async () => onOpen(await extensive.open(search))}>
                            <span>{search.createdAt ? moment(search.createdAt).format('MMM D, YYYY') : NOT_REPORTED}</span>
                            <span className={styles.muted}>{done ? 'Completed' : (search.status || 'Pending').toLowerCase().replace(/^\w/, c => c.toUpperCase()).replace(/_/g, ' ')}</span>
                            <Icon name="chevron-right" size={18} />
                        </button>
                    )
                })}
                {searches.length > 3 && (
                    <div>
                        <Button variant="ghost" size="small" onClick={() => setShowAll(v => !v)}>
                            {showAll ? 'Show fewer' : `Show all ${searches.length}`}
                        </Button>
                    </div>
                )}
            </div>
        </section>
    )
}

export const FmcsaLoading = () => (
    <div className={styles.body} role="status" aria-label="Retrieving FMCSA record">
        <section className={cx(styles.card, styles.skeletonCard)}>
            <Skeleton width="55%" height={28} />
            <Skeleton width="30%" height={14} />
            <div className={styles.skeletonRow}>
                {[0, 1, 2, 3].map(i => <Skeleton key={i} height={72} />)}
            </div>
        </section>
        <div className={styles.grid}>
            <section className={cx(styles.card, styles.skeletonCard)}><Skeleton height={160} /></section>
            <section className={cx(styles.card, styles.skeletonCard)}><Skeleton height={160} /></section>
        </div>
    </div>
)

export const FmcsaNoDot = () => (
    <div className={styles.body}>
        <div className={styles.info}>
            <Icon name="info" />
            <span>Add a DOT number to the application to request FMCSA data</span>
        </div>
    </div>
)

export const FmcsaNotFound = ({ dot, onRequest, requesting, cost }) => (
    <div className={styles.body}>
        <section className={styles.card}>
            <EmptyState
                kind="not-found"
                title="No FMCSA record found"
                description={dot
                    ? `No carrier matches DOT ${dot} in FMCSA. Check the DOT number on the application, then request again.`
                    : 'FMCSA returned no carrier for this application.'}
            />
            {onRequest && (
                <div className={styles.emptyAction}>
                    <Button variant="secondary" loading={requesting} onClick={onRequest}>
                        {requesting ? 'Requesting...' : 'Request FMCSA data'}
                        {!requesting && <CreditCost cost={cost} />}
                    </Button>
                </div>
            )}
        </section>
    </div>
)

const FmcsaSummary = ({ summary, extensive, readOnly, onOpenExtensive }) => (
    <div className={styles.body}>
        <section className={styles.card} aria-label="Carrier summary">
            <div className={styles.summaryTop}>
                <div>
                    <h3 className={styles.carrierName}>{summary.legalName}</h3>
                    {summary.dba && <span className={styles.dba}>DBA {summary.dba}</span>}
                </div>
                <div className={styles.chips}>
                    {summary.chips.map(c => (
                        <span key={c.label} className={styles.chip}>
                            <span className={styles.chipLabel}>{c.label}</span>
                            <span className={cx(styles.chipValue, c.value === NOT_REPORTED && styles.muted)}>{c.value}</span>
                        </span>
                    ))}
                </div>
            </div>
            <div className={styles.verdicts}>
                {summary.verdicts.map(v => (
                    <div key={v.label} className={cx(styles.verdict, styles[v.tone])}>
                        <span className={styles.eyebrow}>{v.label}</span>
                        <Badge tone={v.tone}>{v.badge}</Badge>
                        <span className={styles.verdictDetail}>{v.detail}</span>
                    </div>
                ))}
            </div>
        </section>

        <div className={styles.grid}>
            <Card title="Business">
                {summary.business.map(f => <Field key={f.label} {...f} />)}
            </Card>

            <Card title="Operating authority">
                {summary.authority.map(a => (
                    <div key={a.label} className={styles.field}>
                        <span className={styles.fieldLabel}>{a.label}</span>
                        <span>
                            {a.status
                                ? <Badge tone={a.status.tone}>{a.status.label}</Badge>
                                : <span className={styles.muted}>{NOT_REPORTED}</span>}
                        </span>
                    </div>
                ))}
                <div className={styles.divider} />
                {summary.operation.map(f => <Field key={f.label} {...f} />)}
            </Card>

            <Card title="Insurance" className={styles.full}>
                {summary.insurance.map(row => <Policy key={row.key} row={row} />)}
            </Card>

            <Card title="Safety">
                <div className={styles.field}>
                    <span className={styles.fieldLabel}>Safety rating</span>
                    <span><Badge tone={summary.rating.tone}>{summary.rating.label}</Badge></span>
                </div>
                {summary.safety.map(f => <Field key={f.label} {...f} />)}
                <div className={styles.tiles}>
                    {summary.crashes.map(t => <Tile key={t.label} {...t} />)}
                </div>
                <Field label="Hazmat out-of-service national average" value={summary.hazmat} />
            </Card>

            <Card title="Fleet">
                <div className={styles.tiles}>
                    {summary.fleet.map(t => <Tile key={t.label} {...t} />)}
                </div>
            </Card>
        </div>

        {extensive && <ExtensiveFooter extensive={extensive} readOnly={readOnly} onOpen={onOpenExtensive} />}
    </div>
)

export default FmcsaSummary
